import { type z } from "zod";

const apiBase = (import.meta.env.VITE_API_BASE_URL ?? "/api/v1").replace(/\/$/, "");

// One reload is enough — without the latch every in-flight 401 would
// queue its own navigation.
let sessionExpiredReloading = false;

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function apiFetch<TSchema extends z.ZodTypeAny>(
  path: string,
  schema: TSchema,
  init?: RequestInit,
): Promise<z.infer<TSchema>> {
  const res = await fetch(`${apiBase}${path}`, {
    ...init,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });

  if (!res.ok) {
    let body: unknown = undefined;
    try {
      body = await res.json();
    } catch {
      // ignore
    }
    const message =
      body && typeof body === "object" && "message" in body
        ? String((body as { message: unknown }).message)
        : `HTTP ${res.status}`;
    // A 401 outside the auth endpoints means the session died mid-use
    // (cookie expiry, revocation). Reload the shell once so the app
    // falls back to the sign-in screen instead of limping on with dead
    // data and failing requests. Reloading in place (not to "/") lets
    // SignInPage stash the current URL for post-login restore. /auth/*
    // is excluded: the login flow and the `me` bootstrap handle their
    // own 401s.
    if (res.status === 401 && !path.startsWith("/auth/") && !sessionExpiredReloading) {
      sessionExpiredReloading = true;
      window.location.reload();
    }
    throw new ApiError(res.status, message, body);
  }

  // NestJS sometimes returns 200 + empty body for void handlers
  // (DELETE / PATCH no-content style). Treat any empty body the same
  // as 204 so the schema parse doesn't blow up on "".
  if (res.status === 204) return undefined as z.infer<TSchema>;
  const text = await res.text();
  if (text.length === 0) return undefined as z.infer<TSchema>;
  return schema.parse(JSON.parse(text));
}
